import { useState, useCallback, useRef } from "react";
import { Upload, Loader2, CheckCircle, XCircle, FileImage, Video, Link2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";

export interface UploadResult {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  status: "success" | "error" | "reused";
  error?: string;
}

interface ExistingAsset {
  fileName: string;
  fileUrl: string;
}

interface BulkMediaUploadPanelProps {
  bucketName: string;
  uploadPathPrefix: string;
  accept: string;
  validMimeTypes: string[];
  mediaLabel: string;
  helperText: string;
  existingAssets?: ExistingAsset[];
  /** @deprecated Use existingAssets instead */
  existingFileNames?: string[];
  onUploadComplete: (results: UploadResult[]) => void;
}

export default function BulkMediaUploadPanel({
  bucketName,
  uploadPathPrefix,
  accept,
  validMimeTypes,
  mediaLabel,
  helperText,
  existingAssets = [],
  existingFileNames = [],
  onUploadComplete,
}: BulkMediaUploadPanelProps) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ fileName: string; status: "uploading" | "done" | "error" | "reused" }[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const isVideo = validMimeTypes.some((type) => type.startsWith("video/"));

  // Build lookup from both old and new props
  const existingMap = useCallback(() => {
    const map = new Map<string, string>();
    for (const asset of existingAssets) {
      map.set(asset.fileName.toLowerCase(), asset.fileUrl);
    }
    // Fallback for old prop — no URL available, just marks as existing
    for (const name of existingFileNames) {
      if (!map.has(name.toLowerCase())) {
        map.set(name.toLowerCase(), "");
      }
    }
    return map;
  }, [existingAssets, existingFileNames]);

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files).filter((file) => validMimeTypes.includes(file.type));

    if (fileArray.length === 0) {
      toast.error(`No valid ${mediaLabel.toLowerCase()} files selected`);
      return;
    }

    // De-duplicate within batch
    const seen = new Set<string>();
    const uniqueFiles: File[] = [];
    const batchDupes: string[] = [];
    for (const file of fileArray) {
      const key = file.name.toLowerCase();
      if (seen.has(key)) {
        batchDupes.push(file.name);
      } else {
        seen.add(key);
        uniqueFiles.push(file);
      }
    }

    if (batchDupes.length > 0) {
      toast.warning(`${batchDupes.length} duplicate(s) within this batch skipped`);
    }

    if (uniqueFiles.length === 0) {
      return;
    }

    setUploading(true);
    setProgress(uniqueFiles.map((file) => ({ fileName: file.name, status: "uploading" as const })));

    const existingLookup = existingMap();
    const results: UploadResult[] = [];

    for (let i = 0; i < uniqueFiles.length; i++) {
      const file = uniqueFiles[i];
      const existingUrl = existingLookup.get(file.name.toLowerCase());

      // If the file already exists, reuse it instead of blocking
      if (existingUrl) {
        results.push({
          fileName: file.name,
          fileUrl: existingUrl,
          fileSize: file.size,
          mimeType: file.type,
          status: "reused",
        });
        setProgress((prev) => prev.map((item, index) => (index === i ? { ...item, status: "reused" } : item)));
        continue;
      }

      try {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `${uploadPathPrefix}/${Date.now()}_${safeName}`;

        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(path, file, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from(bucketName).getPublicUrl(path);

        results.push({
          fileName: file.name,
          fileUrl: urlData.publicUrl,
          fileSize: file.size,
          mimeType: file.type,
          status: "success",
        });

        setProgress((prev) => prev.map((item, index) => (index === i ? { ...item, status: "done" } : item)));
      } catch (err: unknown) {
        results.push({
          fileName: file.name,
          fileUrl: "",
          fileSize: file.size,
          mimeType: file.type,
          status: "error",
          error: getErrorMessage(err),
        });

        setProgress((prev) => prev.map((item, index) => (index === i ? { ...item, status: "error" } : item)));
      }
    }

    setUploading(false);

    const successCount = results.filter((r) => r.status === "success").length;
    const reusedCount = results.filter((r) => r.status === "reused").length;
    const errorCount = results.filter((r) => r.status === "error").length;

    if (successCount > 0) toast.success(`${successCount} ${mediaLabel.toLowerCase()} file(s) uploaded`);
    if (reusedCount > 0) toast.info(`${reusedCount} existing asset(s) found and relinked`);
    if (errorCount > 0) toast.error(`${errorCount} file(s) failed`);

    // Pass both new uploads AND reused files so they get linked to lessons
    onUploadComplete(results.filter((r) => r.status === "success" || r.status === "reused"));
    setTimeout(() => setProgress([]), 3000);
  }, [bucketName, existingMap, mediaLabel, onUploadComplete, uploadPathPrefix, validMimeTypes]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  const Icon = isVideo ? Video : FileImage;

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          dragOver
            ? "border-primary bg-primary/5"
            : "border-border hover:border-muted-foreground/40 hover:bg-muted/20"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) processFiles(e.target.files);
            e.target.value = "";
          }}
        />

        <div className="flex flex-col items-center gap-3">
          {uploading ? (
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Upload className="w-6 h-6 text-primary" />
            </div>
          )}
          <div>
            <p className="font-heading font-semibold text-sm text-foreground">
              {uploading ? "Uploading..." : `Drop ${mediaLabel.toLowerCase()} here or click to browse`}
            </p>
            <p className="text-xs text-muted-foreground mt-1 font-body">{helperText}</p>
          </div>
        </div>
      </div>

      {progress.length > 0 && (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {progress.map((item, index) => (
            <div key={`${item.fileName}-${index}`} className="flex items-center gap-2 text-xs font-body px-2 py-1.5 rounded-lg bg-muted/30">
              {item.status === "uploading" && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />}
              {item.status === "done" && <CheckCircle className="w-3.5 h-3.5 text-accent shrink-0" />}
              {item.status === "reused" && <Link2 className="w-3.5 h-3.5 text-primary shrink-0" />}
              {item.status === "error" && <XCircle className="w-3.5 h-3.5 text-destructive shrink-0" />}
              <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="truncate text-muted-foreground">
                {item.fileName}
                {item.status === "reused" && <span className="text-primary ml-1">— relinked</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
